/*
 * Copyright 2026 OpenTrace Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * GitHub REST API client for fetching repository contents.
 *
 * Downloads a zipball via the /fn/archive proxy (single request).
 */

import { unzipSync } from 'fflate';
import {
  extractFilesFromZip,
  fetchResolvedArchive,
  type UrlLoaderOptions,
} from './shared';
import type {
  RepoLoader,
  LoaderInput,
  LoaderCallOptions,
} from './loaderInterface';
import { normalizeRepoUrl } from './urlNormalize';
import type { RepoTree } from '../types';

/** Parse "owner/repo" from a GitHub URL. */
export function parseGitHubUrl(
  url: string,
): { owner: string; repo: string } | null {
  // Repo names may contain dots (e.g. "react.dev"); stop only at a path
  // separator / query / fragment, and drop an optional trailing ".git".
  const match = url.match(
    /github\.com[/:]([^/]+)\/([^/]+?)(?:\.git)?(?:[/?#]|$)/,
  );
  if (!match) return null;
  return { owner: match[1], repo: match[2] };
}

/** Fetch a full repo tree. Uses zipball by default. */
export async function fetchRepoTree(
  owner: string,
  repo: string,
  options: UrlLoaderOptions = {},
): Promise<RepoTree> {
  return fetchViaZipball(owner, repo, options);
}

// ---------------------------------------------------------------------------
// Zipball mode (via /fn/archive proxy)
// ---------------------------------------------------------------------------

async function fetchViaZipball(
  owner: string,
  repo: string,
  options: UrlLoaderOptions,
): Promise<RepoTree> {
  const { token, ref = 'HEAD', signal, onProgress } = options;

  const params = new URLSearchParams({
    owner,
    repo,
    ref,
    provider: 'github',
    mode: 'resolve',
  });
  const base =
    import.meta.env.VITE_ARCHIVE_URL || 'https://oss.opentrace.ai/fn/archive';
  const resolveUrl = `${base}?${params}`;
  const fetchHeaders: Record<string, string> = {};
  if (token) {
    fetchHeaders.Authorization = `Bearer ${token}`;
  }

  const { data, gitMeta } = await fetchResolvedArchive(
    resolveUrl,
    fetchHeaders,
    signal,
    onProgress,
  );
  const entries = unzipSync(data);

  const files = extractFilesFromZip(entries, onProgress);

  return {
    owner,
    repo,
    ref,
    url: `https://github.com/${owner}/${repo}`,
    provider: 'github',
    sha: gitMeta.sha,
    branch: gitMeta.branch,
    commitMessage: gitMeta.commitMessage?.split('\n', 1)[0],
    files,
  };
}

// ---------------------------------------------------------------------------
// RepoLoader implementation
// ---------------------------------------------------------------------------

export const githubLoader: RepoLoader = {
  name: 'github',

  canHandle(input: LoaderInput): boolean {
    if (input.kind !== 'url') return false;
    const normalized = normalizeRepoUrl(input.url);
    return parseGitHubUrl(normalized) !== null;
  },

  async load(
    input: LoaderInput,
    options: LoaderCallOptions,
  ): Promise<RepoTree> {
    if (input.kind !== 'url')
      throw new Error('GitHub loader requires a URL input');
    const normalized = normalizeRepoUrl(input.url);
    const parsed = parseGitHubUrl(normalized);
    if (!parsed) throw new Error('Not a valid GitHub URL');
    return fetchRepoTree(parsed.owner, parsed.repo, {
      token: input.token,
      ref: input.ref || 'HEAD',
      signal: options.signal,
      onProgress: options.onProgress,
    });
  },
};
